import { z } from "zod";
import {
	type OfferKind,
	offerAppliesToSchema,
	offerDiscountTypeSchema,
	offerKindSchema,
} from "./enums";
import { timeOfDaySchema } from "./restaurant";

// The shape of `imageRefSchema` in catalog.ts, which imports this module.
const imageRefSchema = z.object({ id: z.uuid(), blurhash: z.string().nullable() });

/**
 * Offers (MG-05, SF-13): item offers, bill offers and coupons, with a
 * schedule in the restaurant's time zone. Which offer applies, and how
 * much it takes off, is decided by `@luckymenu/billing-core/offers`. See
 * docs/architecture/data-model.md, Promotions, and api.md, Promotions.
 */

const uniqueIds = (ids: string[]) => new Set(ids).size === ids.length;

/** Most targets an item offer may name. */
export const MAX_OFFER_TARGETS = 200;

/** Suggested add-ons an item may have (P9-T11). */
export const MAX_ITEM_SUGGESTIONS = 4;

/** A coupon code: 3 to 20 letters or digits, stored in capitals. */
export const couponCodeSchema = z
	.string()
	.trim()
	.regex(/^[A-Za-z0-9]{3,20}$/, "3 to 20 letters or digits")
	.transform((v) => v.toUpperCase());

/** ISO weekdays: 1 is Monday, 7 is Sunday. */
export const isoDaySchema = z.number().int().min(1).max(7);

/**
 * When an offer runs, in the restaurant's time zone. Every part is optional:
 * no dates run from now on and for good, no days every day, no hours all
 * day. A window whose end is before its start runs past midnight and
 * belongs to the day it starts on.
 */
export const offerScheduleShape = {
	/** The first moment it applies. Null: from when it is switched on. */
	startsAt: z.iso.datetime().nullable(),
	/** The moment it stops (not included). Null: until switched off. */
	endsAt: z.iso.datetime().nullable(),
	/** ISO weekdays it runs on. Empty: every day. */
	daysOfWeek: z.array(isoDaySchema),
	/** "HH:MM", both or neither. */
	timeFrom: timeOfDaySchema.nullable(),
	timeTo: timeOfDaySchema.nullable(),
};

/** An offer as staff see it (More > Offers). */
export const offerSchema = z.object({
	id: z.uuid(),
	name: z.string(),
	description: z.string().nullable(),
	kind: offerKindSchema,
	discountType: offerDiscountTypeSchema,
	/** Basis points for `percent`, paise for `flat` and `fixed_price`. */
	value: z.number().int(),
	/** Bill offers and coupons: the bill (after item offers) must reach this. */
	minOrderPaise: z.number().int().nullable(),
	/** `percent` only: the most it takes off (each unit for an item offer). */
	maxDiscountPaise: z.number().int().nullable(),
	/** Coupons only. */
	code: z.string().nullable(),
	appliesTo: offerAppliesToSchema,
	itemIds: z.array(z.uuid()),
	categoryIds: z.array(z.uuid()),
	...offerScheduleShape,
	/** The owner's switch. */
	isActive: z.boolean(),
	/** Shown in the menu's carousel and the website's offers section. */
	showOnMenu: z.boolean(),
	banner: imageRefSchema.nullable(),
	sortOrder: z.number().int(),
	createdAt: z.iso.datetime(),
	updatedAt: z.iso.datetime(),
});
export type Offer = z.infer<typeof offerSchema>;

export const offerListSchema = z.object({
	offers: z.array(offerSchema),
	/** The restaurant's time zone, to say which offers run now. */
	timeZone: z.string(),
});
export type OfferList = z.infer<typeof offerListSchema>;

const optionalText = (max: number) =>
	z
		.string()
		.trim()
		.max(max)
		.transform((v) => (v === "" ? null : v))
		.nullable()
		.optional();

/**
 * `staff.promotions.upsert`: create or replace an offer by a client-made ID.
 * The rules between fields are checked here, so the form shows them at once.
 */
export const upsertOfferInputSchema = z
	.object({
		id: z.uuid(),
		name: z.string().trim().min(1).max(60),
		description: optionalText(200),
		kind: offerKindSchema,
		discountType: offerDiscountTypeSchema,
		value: z.number().int().min(0).max(10_000_000),
		minOrderPaise: z.number().int().min(1).max(10_000_000).nullable().default(null),
		maxDiscountPaise: z.number().int().min(1).max(10_000_000).nullable().default(null),
		code: couponCodeSchema.nullable().default(null),
		appliesTo: offerAppliesToSchema.default("all"),
		itemIds: z.array(z.uuid()).max(MAX_OFFER_TARGETS).refine(uniqueIds).default([]),
		categoryIds: z.array(z.uuid()).max(MAX_OFFER_TARGETS).refine(uniqueIds).default([]),
		startsAt: z.iso.datetime().nullable().default(null),
		endsAt: z.iso.datetime().nullable().default(null),
		daysOfWeek: z
			.array(isoDaySchema)
			.max(7)
			.refine((d) => new Set(d).size === d.length, "Each day once")
			.transform((d) => [...d].sort((a, b) => a - b))
			.default([]),
		timeFrom: timeOfDaySchema.nullable().default(null),
		timeTo: timeOfDaySchema.nullable().default(null),
		isActive: z.boolean().default(true),
		showOnMenu: z.boolean().default(true),
		/** An uploaded `offer_banner`, or null for none. */
		bannerFileId: z.uuid().nullable().default(null),
	})
	.superRefine((o, ctx) => {
		const issue = (path: string, message: string) =>
			ctx.addIssue({ code: "custom", path: [path], message });
		const isItem = o.kind === "item_discount";
		if (o.discountType === "percent" && (o.value < 1 || o.value > 10_000)) {
			issue("value", "Between 0.01% and 100%");
		}
		if (o.discountType === "flat" && o.value < 1) issue("value", "More than ₹0");
		if (o.discountType === "fixed_price" && !isItem) {
			issue("discountType", "A fixed price is for item offers");
		}
		if (o.discountType !== "percent" && o.maxDiscountPaise !== null) {
			issue("maxDiscountPaise", "Only a percentage has a cap");
		}
		if (isItem && o.minOrderPaise !== null) {
			issue("minOrderPaise", "Item offers have no minimum order");
		}
		if ((o.kind === "coupon") !== (o.code !== null)) {
			issue("code", o.kind === "coupon" ? "A coupon needs a code" : "Only coupons have a code");
		}
		if (!isItem && o.appliesTo !== "all") issue("appliesTo", "Bill offers apply to the bill");
		if (o.appliesTo === "items" && o.itemIds.length === 0) issue("itemIds", "Choose the dishes");
		if (o.appliesTo === "categories" && o.categoryIds.length === 0) {
			issue("categoryIds", "Choose the categories");
		}
		if (o.startsAt && o.endsAt && o.endsAt <= o.startsAt) {
			issue("endsAt", "The end must be after the start");
		}
		if ((o.timeFrom === null) !== (o.timeTo === null)) {
			issue("timeTo", "Give both times, or neither");
		}
		if (o.timeFrom !== null && o.timeFrom === o.timeTo) {
			issue("timeTo", "The end must differ from the start");
		}
	})
	.transform((o) => ({
		...o,
		itemIds: o.appliesTo === "items" ? o.itemIds : [],
		categoryIds: o.appliesTo === "categories" ? o.categoryIds : [],
	}));
export type UpsertOfferInput = z.input<typeof upsertOfferInputSchema>;

export const setOfferActiveInputSchema = z.object({ id: z.uuid(), isActive: z.boolean() });

/**
 * An offer as diners see it: in `PublicMenu.offers` and from
 * `public.promotions.listActiveOffers`. Coupon codes are never listed.
 */
export const publicOfferSchema = z.object({
	id: z.uuid(),
	name: z.string(),
	description: z.string().nullable(),
	kind: offerKindSchema,
	discountType: offerDiscountTypeSchema,
	value: z.number().int(),
	minOrderPaise: z.number().int().nullable(),
	maxDiscountPaise: z.number().int().nullable(),
	appliesTo: offerAppliesToSchema,
	itemIds: z.array(z.uuid()),
	categoryIds: z.array(z.uuid()),
	...offerScheduleShape,
	showOnMenu: z.boolean(),
	banner: imageRefSchema.nullable(),
});
export type PublicOffer = z.infer<typeof publicOfferSchema>;

export const activeOffersSchema = z.object({
	/** Offers running now, in the owner's order. */
	offers: z.array(publicOfferSchema),
});
export type ActiveOffers = z.infer<typeof activeOffersSchema>;

/** `public.promotions.applyCoupon`: a code typed in the cart. */
export const applyCouponInputSchema = z.object({
	code: z.string().trim().min(1).max(40),
	/** The cart's value after item offers, to say how far it is from the minimum. */
	cartValuePaise: z.number().int().min(0).max(100_000_000).optional(),
});
export type ApplyCouponInput = z.input<typeof applyCouponInputSchema>;

export const appliedCouponSchema = z.object({
	code: z.string(),
	offer: publicOfferSchema,
	/** How much more the cart needs for the coupon to apply; 0 when it applies. */
	morePaise: z.number().int(),
});
export type AppliedCoupon = z.infer<typeof appliedCouponSchema>;

// ---------------------------------------------------------------------------
// Offers in a cart quote and on the bill.
// ---------------------------------------------------------------------------

/** The item offer applied to a quote line or an order item: so much off each unit. */
export const lineOfferSchema = z.object({
	offerId: z.uuid(),
	name: z.string(),
	unitDiscountPaise: z.number().int(),
});
export type LineOffer = z.infer<typeof lineOfferSchema>;

/** A bill offer applied to a quote or a bill preview. */
export const billOfferSchema = z.object({
	offerId: z.uuid(),
	name: z.string(),
	kind: offerKindSchema,
	amountPaise: z.number().int(),
});
export type BillOffer = z.infer<typeof billOfferSchema>;

/** "Add ₹60 more to get 10% off" (P9-T14). */
export const offerNudgeSchema = z.object({
	offerId: z.uuid(),
	name: z.string(),
	discountType: offerDiscountTypeSchema,
	value: z.number().int(),
	maxDiscountPaise: z.number().int().nullable(),
	morePaise: z.number().int(),
});
export type OfferNudge = z.infer<typeof offerNudgeSchema>;

/** What a cart's coupon does now: applied, waiting for the minimum, or not usable. */
export const quoteCouponSchema = z.object({
	code: z.string(),
	status: z.enum(["applied", "min_order", "not_usable", "better_offer"]),
	/** For `min_order`. */
	morePaise: z.number().int(),
});
export type QuoteCoupon = z.infer<typeof quoteCouponSchema>;

export const quoteOffersSchema = z.object({
	/** The bill offer the cart gets now, if any. */
	bill: billOfferSchema.nullable(),
	nudge: offerNudgeSchema.nullable(),
	coupon: quoteCouponSchema.nullable(),
});
export type QuoteOffers = z.infer<typeof quoteOffersSchema>;

/** Item offers are decided per unit; bill offers and coupons on the bill. */
export function isBillOfferKind(kind: OfferKind): boolean {
	return kind !== "item_discount";
}

// ---------------------------------------------------------------------------
// The Offers report (P9-T20).
// ---------------------------------------------------------------------------

export const offersReportRowSchema = z.object({
	offerId: z.uuid(),
	/** As printed on the bills. */
	name: z.string(),
	kind: offerKindSchema,
	/** Final bills that carry it. */
	bills: z.number().int(),
	discountPaise: z.number().int(),
});

export const offersReportSchema = z.object({
	offers: z.array(offersReportRowSchema),
	/** Units on final bills by how they were added. */
	items: z.object({
		total: z.number().int(),
		fromSuggestions: z.number().int(),
		fromReorder: z.number().int(),
	}),
});
export type OffersReport = z.infer<typeof offersReportSchema>;
